<script setup lang="ts">
  import { inject, ref, watch } from 'vue';
  import type { OrderOptions, Pagination as PaginationMeta } from '@/shared/lib';
  import {
    Checkbox,
    HeaderTableWithButtons,
    IconImage,
    InfiniteScrollView,
    Pagination,
    SearchNoResults,
    SelectInput,
    SimpleLoading,
    TableHeader,
    Tooltip,
  } from '@/shared/ui';
  import SmallChevronUpIcon from '@/shared/assets/icons/SmallChevronUp.svg';
  import EyeIcon from '@/shared/assets/icons/EyeActionIcon.svg';
  import type { RecordItem, RecordRevision } from '@/entities/Record';
  import {
    AddRecordFilterInjectionKey,
    createAddRecordFilterContext,
  } from '../model/addRecordFilterContext';
  import AddRecordFilterPanel from './AddRecordFilterPanel.vue';

  const props = withDefaults(
    defineProps<{
      records: RecordItem[];
      orderOptions?: OrderOptions[];
      quantityOptions: Array<{ id: number; name: string }>;
      selectedQuantity: number;
      totalRecordsLabel: string;
      metadata?: PaginationMeta;
      currentPage?: number;
      isInfiniteScrollMode?: boolean;
      hasMoreItems?: boolean;
      loading?: boolean;
      loadingMore?: boolean;
      allSelected?: boolean;
      partiallySelected?: boolean;
      isRevisionSelected: (revisionId: number) => boolean;
      /** False once the selection cap is reached, so an unticked row cannot be ticked. */
      canSelectRevision?: (revisionId: number) => boolean;
      isRecordExpanded: (id: number) => boolean;
      getSortedOlderRevisions: (record: RecordItem) => RecordRevision[];
    }>(),
    {
      orderOptions: () => [],
      metadata: undefined,
      currentPage: 1,
      isInfiniteScrollMode: false,
      hasMoreItems: false,
      loading: false,
      loadingMore: false,
      allSelected: false,
      partiallySelected: false,
      canSelectRevision: undefined,
    }
  );

  const emit = defineEmits<{
    (e: 'update:sort', sortField: string, orderFlow: string): void;
    (e: 'update:quantity', quantity: number): void;
    (e: 'toggle-select', revisionId: number): void;
    (e: 'toggle-select-all'): void;
    (e: 'toggle-expand', id: number): void;
    (e: 'view', revision: RecordRevision): void;
    (e: 'page', page: number): void;
  }>();

  /**
   * The rows and the total the table paints while a request is in flight. Without them every
   * page change blinks the empty state, because the store empties the list before the answer
   * arrives.
   */
  const stableRecords = ref(props.records);
  const stableMetadata = ref(props.metadata);

  watch(
    [() => props.records, () => props.loading],
    ([records, loading]) => {
      if (records.length > 0 || !loading) {
        stableRecords.value = records;
      }
    }
  );

  watch(
    [() => props.metadata, () => props.loading],
    ([metadata, loading]) => {
      if (!loading || (metadata?.total ?? 0) > 0) {
        stableMetadata.value = metadata;
      }
    }
  );

  const { activeFilters, isFilterActive, isFilterPanelOpen, toggleFilterPanel, applyFilters } =
    inject(AddRecordFilterInjectionKey, () => createAddRecordFilterContext(), true);

  function hasOlderRevisions(record: RecordItem): boolean {
    return record.olderRevisions.length > 0;
  }
</script>

<template>
  <div class="mt-6">
    <div
      data-testid="add-record-table"
      class="relative overflow-x-auto rounded-2xl border border-gray-02 bg-white-01"
    >
      <div
        v-if="loading && stableRecords.length > 0"
        data-testid="add-record-table-loading-overlay"
        class="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white-01/70"
      >
        <SimpleLoading />
      </div>

      <table class="min-w-full table-fixed">
        <colgroup>
          <col class="w-12">
          <col class="w-24">
          <col>
          <col class="w-40">
          <col class="w-18">
          <col class="w-24">
        </colgroup>
        <thead>
          <tr>
            <HeaderTableWithButtons
              colspan="6"
              text="Selecione os Registros que deseja adicionar"
              :order-options="orderOptions"
              order-option-class="font-normal"
              :has-filter="true"
              :is-filter-active="isFilterActive || isFilterPanelOpen"
              :bold-title="false"
              @update:sort="(sortField, orderFlow) => emit('update:sort', sortField, orderFlow)"
              @filter-clicked="toggleFilterPanel"
            >
              <template #extra-controls>
                <SelectInput
                  :model-value="selectedQuantity"
                  :options="quantityOptions"
                  container-class="w-auto"
                  button-class="bg-white-01 border border-gray-02 whitespace-nowrap shadow-xs font-normal"
                  @update:model-value="(quantity) => emit('update:quantity', Number(quantity))"
                />
              </template>
            </HeaderTableWithButtons>
          </tr>
          <tr>
            <th
              colspan="6"
              class="p-0 transition-[border-color] duration-300 ease-in-out"
              :class="
                isFilterPanelOpen ? 'border-b-2 border-(--theme-secondary-ink)' : 'border-b-0 border-transparent'
              "
            >
              <div
                data-testid="add-record-filter-panel-wrapper"
                class="grid transition-[grid-template-rows] duration-300 ease-in-out"
                :class="isFilterPanelOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
                :inert="isFilterPanelOpen ? undefined : true"
              >
                <div class="overflow-hidden">
                  <AddRecordFilterPanel
                    :current-filters="activeFilters"
                    :is-open="isFilterPanelOpen"
                    @clear="applyFilters({})"
                    @apply="applyFilters"
                  />
                </div>
              </div>
            </th>
          </tr>
          <tr v-if="stableRecords.length > 0">
            <TableHeader
              text="Registro"
              :checkbox="true"
              :checked="allSelected"
              :indeterminate="partiallySelected"
              th-checkbox-class="w-12 align-middle text-center"
              th-class="pl-4 pr-4 py-4.5 text-left text-xs font-semibold text-gray-07"
              @toggle-all="emit('toggle-select-all')"
            />
            <TableHeader
              text="Título"
              th-class="px-4 py-4.5 text-left text-xs font-semibold text-gray-07"
            />
            <TableHeader
              text="Indexação"
              th-class="px-4 py-4.5 text-left text-xs font-semibold text-gray-07"
            />
            <TableHeader
              text="Rev."
              th-class="px-4 py-4.5 text-left text-xs font-semibold text-gray-07"
            />
            <TableHeader
              text="Ações"
              th-class="px-4 py-4.5 text-left text-xs font-semibold text-gray-07"
            />
          </tr>
        </thead>

        <tbody class="divide-y divide-gray-02 border-t border-gray-02 bg-white-01">
          <template v-if="stableRecords.length > 0">
            <template
              v-for="record in stableRecords"
              :key="record.id"
            >
              <tr :data-testid="`record-row-${record.id}`">
                <td class="py-4.5 text-center">
                  <Checkbox
                    :id="record.latestRevision.id"
                    :checked="isRevisionSelected(record.latestRevision.id)"
                    :disabled="!(props.canSelectRevision?.(record.latestRevision.id) ?? true)"
                    @toggle="emit('toggle-select', record.latestRevision.id)"
                  />
                </td>
                <td class="py-4.5 pl-4 text-sm text-gray-07">
                  {{ record.code }}
                </td>
                <td class="px-4 py-4.5 text-sm text-gray-07">
                  {{ record.title }}
                </td>
                <td class="px-4 py-4.5 text-sm text-gray-07">
                  {{ record.indexing }}
                </td>
                <td class="px-4 py-4.5 text-sm text-gray-07">
                  {{ record.latestRevision.revision }}
                </td>
                <td class="px-4 py-4.5">
                  <div class="flex items-center gap-4">
                    <Tooltip content="Visualizar" placement="top">
                      <button
                        type="button"
                        :data-testid="`view-record-${record.id}`"
                        class="inline-flex shrink-0 items-center justify-center p-0 text-gray-03 transition-opacity hover:opacity-70"
                        aria-label="Visualizar"
                        @click="emit('view', record.latestRevision)"
                      >
                        <IconImage
                          :src="EyeIcon"
                          alt="Visualizar"
                          class="h-6 w-6"
                        />
                      </button>
                    </Tooltip>
                    <button
                      v-if="hasOlderRevisions(record)"
                      type="button"
                      :data-testid="`expand-toggle-${record.id}`"
                      class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-02 bg-white-01 text-gray-07 shadow-sm transition-colors hover:bg-gray-01"
                      :aria-expanded="isRecordExpanded(record.id)"
                      :aria-label="isRecordExpanded(record.id) ? 'Recolher revisões' : 'Expandir revisões'"
                      @click="emit('toggle-expand', record.id)"
                    >
                      <IconImage
                        :src="SmallChevronUpIcon"
                        alt=""
                        class="h-4 w-4 transition-transform duration-200"
                        :class="{ 'rotate-180': !isRecordExpanded(record.id) }"
                      />
                    </button>
                  </div>
                </td>
              </tr>
              <tr
                v-for="revision in isRecordExpanded(record.id) ? getSortedOlderRevisions(record) : []"
                :key="revision.id"
                :data-testid="`older-revision-${revision.id}`"
                class="bg-gray-09"
              >
                <td class="py-4.5 text-center">
                  <Checkbox
                    :id="revision.id"
                    :checked="isRevisionSelected(revision.id)"
                    :disabled="!(props.canSelectRevision?.(revision.id) ?? true)"
                    @toggle="emit('toggle-select', revision.id)"
                  />
                </td>
                <td class="py-4.5 pl-4 text-sm text-gray-07">
                  {{ record.code }}
                </td>
                <td class="px-4 py-4.5 text-sm text-gray-07">
                  {{ record.title }}
                </td>
                <td class="px-4 py-4.5 text-sm text-gray-07">
                  {{ record.indexing }}
                </td>
                <td class="px-4 py-4.5 text-sm text-gray-07">
                  {{ revision.revision }}
                </td>
                <td class="px-4 py-4.5">
                  <div class="flex items-center gap-4">
                    <Tooltip content="Visualizar" placement="top">
                      <button
                        type="button"
                        :data-testid="`view-revision-${revision.id}`"
                        class="inline-flex shrink-0 items-center justify-center p-0 text-gray-03 transition-opacity hover:opacity-70"
                        aria-label="Visualizar"
                        @click="emit('view', revision)"
                      >
                        <IconImage
                          :src="EyeIcon"
                          alt="Visualizar"
                          class="h-6 w-6"
                        />
                      </button>
                    </Tooltip>
                  </div>
                </td>
              </tr>
            </template>
          </template>
          <tr v-else-if="loading">
            <td
              colspan="6"
              class="px-6 py-12"
            >
              <div
                data-testid="add-record-table-loading"
                class="flex items-center justify-center"
              >
                <SimpleLoading />
              </div>
            </td>
          </tr>
          <tr v-else>
            <td
              colspan="6"
              class="px-6 py-12"
            >
              <SearchNoResults />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <InfiniteScrollView
      v-if="isInfiniteScrollMode && stableRecords.length > 0"
      :all-items="stableRecords"
      :loading-more="loadingMore"
      :has-more-items="hasMoreItems"
      :metadata="stableMetadata"
      entity-name="registro"
    />

    <Pagination
      v-else-if="stableMetadata && stableRecords.length > 0"
      :total-items="stableMetadata.total"
      :items-per-page="selectedQuantity"
      :current-page="currentPage"
      entity-name="registro"
      @page-changed="emit('page', $event)"
    />

    <p
      data-testid="add-record-total"
      class="mt-4 pl-4 text-sm text-gray-03"
      aria-live="polite"
    >
      Total: {{ totalRecordsLabel }}
    </p>
  </div>
</template>
